import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { TypedUseQueryHookResult } from '@reduxjs/toolkit/query/react';

import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
import type { IGetProfilesResponse } from 'redux-store/api/profiles/profiles.types';
import { useGetProfilesQuery } from 'redux-store/api/profiles/profiles.api';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { authSelector } from 'redux-store/selector';

type TUseProfilesQueryOptions = Parameters<typeof useGetProfilesQuery>[1];

type TProfilesBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

export type TUseProfilesQueryResult = TypedUseQueryHookResult<
  IGetProfilesResponse,
  void,
  TProfilesBaseQuery
>;

export const useProfilesQuery = (options?: TUseProfilesQueryOptions): TUseProfilesQueryResult => {
  const { companyId } = useAppSelector(authSelector);

  const { data: userPermissions, isLoading: isPermissionsLoading } =
    useGetCompanyRolePermissionsMeQuery(undefined, {
      skip: !companyId,
    });

  const shouldSkipProfiles =
    !companyId || !userPermissions?.USER_MANAGEMENT?.canRead;

  const result = useGetProfilesQuery(undefined, {
    ...options,
    skip: options?.skip || shouldSkipProfiles,
  });

  return {
    ...result,
    isLoading: isPermissionsLoading || result.isLoading,
  } as TUseProfilesQueryResult;
};
